import React, { useState, useEffect, useMemo } from 'react';
import { X, Tag, Palette, Users, Loader2, Check, Trash2, GitMerge, Pencil, Plus, Shield, Compass, Search, User, Plug, Target, RotateCcw, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags, FEATURE_IDS } from '../../hooks/useFeatureFlags';
import { productTagsApi, productsApi } from '../../services/api';
import { toast } from 'sonner';
import { ProductNavigationSettings } from '../settings/NavigationSettings';
import ProductIntegrationsSection from './ProductIntegrationsSection';

// Grace period options (in days)
const GRACE_PERIOD_OPTIONS = [
  { value: 2, label: '2 days' },
  { value: 5, label: '5 days' },
  { value: 7, label: '1 week' },
  { value: 14, label: '2 weeks' },
  { value: 21, label: '3 weeks' },
  { value: 30, label: '30 days' },
];

// Color palette for product cards
const CARD_COLORS = [
  { value: '#6366f1', name: 'Indigo' },
  { value: '#8b5cf6', name: 'Violet' },
  { value: '#a855f7', name: 'Purple' },
  { value: '#ec4899', name: 'Pink' },
  { value: '#ef4444', name: 'Red' },
  { value: '#f97316', name: 'Orange' },
  { value: '#eab308', name: 'Yellow' },
  { value: '#22c55e', name: 'Green' },
  { value: '#14b8a6', name: 'Teal' },
  { value: '#06b6d4', name: 'Cyan' },
  { value: '#3b82f6', name: 'Blue' },
  { value: '#64748b', name: 'Slate' },
];

// Available tabs for default launch
const LAUNCH_TABS = [
  { value: 'ideation', label: 'Ideation' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'market', label: 'Market' },
  { value: 'personas', label: 'Personas' },
  { value: 'goals', label: 'Goals' },
  { value: 'features', label: 'Features' },
  { value: 'epics', label: 'Epics' },
  { value: 'releases', label: 'Releases' },
];

// Role badge colors
const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  product_manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  collaborator: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

// Format role for display
const formatRole = (role) => {
  if (!role) return 'Member';
  return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// Access Member Selector Component
const AccessMemberSelector = ({ teamMembers, selectedMembers, setSelectedMembers, currentUserId, colors }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter members based on search query
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return teamMembers;
    const query = searchQuery.toLowerCase();
    return teamMembers.filter(member => 
      member.name?.toLowerCase().includes(query) ||
      member.email?.toLowerCase().includes(query) ||
      member.role?.toLowerCase().includes(query)
    );
  }, [teamMembers, searchQuery]);
  
  // Sort: selected first, then by name
  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      const aSelected = selectedMembers.includes(a.id);
      const bSelected = selectedMembers.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [filteredMembers, selectedMembers]);
  
  // Auto-select current user on mount if not already selected
  useEffect(() => {
    const currentUserMember = teamMembers.find(m => m.user_id === currentUserId || m.id === currentUserId);
    if (currentUserMember && !selectedMembers.includes(currentUserMember.id)) {
      setSelectedMembers(prev => {
        if (prev.includes(currentUserMember.id)) return prev;
        return [...prev, currentUserMember.id];
      });
    }
  // Only run on mount and when teamMembers/currentUserId change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMembers, currentUserId]);
  
  const handleToggleMember = (memberId, isCurrentUser) => {
    if (isCurrentUser) {
      // Prevent deselecting yourself
      return;
    }
    
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };
  
  return (
    <div className="space-y-3">
      {/* Search Input */}
      {teamMembers.length > 5 && (
        <div className="relative">
          <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or role..."
            className={cn("pl-9 h-9", colors.bgInput, colors.borderPrimary)}
          />
        </div>
      )}
      
      {/* Selected count */}
      <div className={cn("text-xs", colors.textMuted)}>
        {selectedMembers.length} of {teamMembers.length} members selected
      </div>
      
      {/* Members List */}
      <div className={cn("max-h-64 overflow-y-auto space-y-1 rounded-lg border p-2", colors.borderPrimary, colors.bgTertiary)}>
        {sortedMembers.length === 0 ? (
          <p className={cn("text-sm italic py-4 text-center", colors.textMuted)}>
            {searchQuery ? 'No members match your search' : 'No team members found'}
          </p>
        ) : (
          sortedMembers.map(member => {
            const isCurrentUser = member.user_id === currentUserId || member.id === currentUserId;
            const isSelected = selectedMembers.includes(member.id);
            const roleColor = ROLE_COLORS[member.role] || ROLE_COLORS.collaborator;
            
            return (
              <div
                key={member.id}
                onClick={() => handleToggleMember(member.id, isCurrentUser)}
                className={cn(
                  "flex items-center gap-3 p-2 rounded-md transition-colors",
                  isCurrentUser ? "cursor-not-allowed opacity-70" : "cursor-pointer",
                  isSelected ? "bg-indigo-50 dark:bg-indigo-900/20" : "hover:bg-gray-100 dark:hover:bg-gray-700"
                )}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={isCurrentUser}
                  onChange={() => {}}
                  className="text-indigo-600 rounded pointer-events-none"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn("text-sm font-medium truncate", colors.textPrimary)}>
                      {member.name || 'Unknown'}
                    </span>
                    {isCurrentUser && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        You
                      </span>
                    )}
                    <span className={cn("text-xs px-1.5 py-0.5 rounded", roleColor)}>
                      {formatRole(member.role)}
                    </span>
                  </div>
                  <p className={cn("text-xs truncate", colors.textMuted)}>
                    {member.email}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Export Tab Content Component
const ExportTabContent = ({ productId, productName, colors }) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await productsApi.exportAiContext(productId);
      const blob = new Blob([res.data], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (productName || 'product').replace(/[^a-zA-Z0-9 _-]/g, '_').replace(/\s+/g, '_');
      a.href = url;
      a.download = `${safeName}_ai_context.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Export for AI</h3>
        <p className={cn("text-sm mb-6", colors.textMuted)}>
          Download a structured Markdown file containing your product's strategy, market data, and personas — optimized for use with external AI tools like marketing copilots, content generators, and strategic planners.
        </p>
      </div>

      <div className={cn("p-4 rounded-lg border space-y-3", colors.bgTertiary, colors.borderPrimary)}>
        <h4 className={cn("text-sm font-semibold", colors.textPrimary)}>AI Context Export (.md)</h4>
        <p className={cn("text-xs", colors.textMuted)}>
          Includes: Vision, Value Proposition, Target Audience, Key Differentiators, Strategic Pillars, Business Model, Market Segments, Competitors, SWOT Analysis, and all Personas with their linked market segment details inlined for full context.
        </p>
        <Button
          data-testid="export-ai-context-btn"
          onClick={handleExport}
          disabled={exporting}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {exporting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
          ) : (
            <><Download className="w-4 h-4 mr-2" />Download AI Context (.md)</>
          )}
        </Button>
      </div>
    </div>
  );
};

const ProductSettingsModal = ({ 
  open, 
  onClose, 
  productId, 
  product,
  teamMembers = [],
  onProductUpdate 
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const { organization, user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const isFreeProduct = organization?.account_type === 'free_product';  
  // Get product ID from prop or product object
  const resolvedProductId = productId || product?.product_id;
  
  // Default to navigation tab if available, otherwise tags
  const orgDefaults = organization?.navigation_defaults || {};
  const canShowNavigation = orgDefaults.allow_product_override !== false || 
    user?.role === 'owner' || user?.role === 'product_manager' || user?.role === 'admin';
  const [activeTab, setActiveTab] = useState(isFreeProduct ? 'reset' : 'general');
  const [loading, setLoading] = useState(false);
  
  // Start Over state (for free product Reset tab)
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [startingOver, setStartingOver] = useState(false);
  
  // Tags state
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [editingTag, setEditingTag] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('');
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null);
  
  // New tag creation state
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [creatingTag, setCreatingTag] = useState(false);
  
  // Product settings state
  const [cardColor, setCardColor] = useState(product?.card_color || '#6366f1');
  const [accessMode, setAccessMode] = useState(product?.access_mode || 'everyone');
  const [selectedMembers, setSelectedMembers] = useState(product?.allowed_members || []);
  
  // User-specific preferences (For You tab)
  const [userDefaultTab, setUserDefaultTab] = useState(null);
  const [loadingUserPrefs, setLoadingUserPrefs] = useState(false);
  const [savingUserPrefs, setSavingUserPrefs] = useState(false);
  
  // Navigation settings state
  const [navigationSettings, setNavigationSettings] = useState(product?.navigation_settings || null);
  
  // Load user preferences
  useEffect(() => {
    if (open && resolvedProductId) {
      loadUserPreferences();
    }
  }, [open, resolvedProductId]);
  
  const loadUserPreferences = async () => {
    setLoadingUserPrefs(true);
    try {
      const res = await productsApi.getUserPreferences(resolvedProductId);
      setUserDefaultTab(res.data?.default_tab || null);
    } catch (err) {
      console.error('Error loading user preferences:', err);
    } finally {
      setLoadingUserPrefs(false);
    }
  };
  
  const saveUserPreferences = async () => {
    setSavingUserPrefs(true);
    try {
      await productsApi.updateUserPreferences(resolvedProductId, {
        default_tab: userDefaultTab
      });
      toast.success('Your preferences saved');
    } catch (err) {
      console.error('Error saving user preferences:', err);
      toast.error('Failed to save preferences');
    } finally {
      setSavingUserPrefs(false);
    }
  };
  
  // Stability settings state
  const [goalGracePeriod, setGoalGracePeriod] = useState(
    product?.settings?.goal_grace_period_days || 2
  );
  
  // Product Owner settings state
  const [productOwnerId, setProductOwnerId] = useState(
    product?.settings?.product_owner_id || ''
  );
  const [kpiReminderEnabled, setKpiReminderEnabled] = useState(
    product?.settings?.kpi_reminder_enabled || false
  );
  
  // Check if user can edit navigation (owner or PM - includes team role)
  const canEditNavigation = user?.role === 'owner' || user?.role === 'product_manager' || user?.role === 'admin' || user?.teamRole === 'owner';
  
  // Load tags
  useEffect(() => {
    if (open && resolvedProductId) {
      loadTags();
    }
  }, [open, resolvedProductId]);
  
  const loadTags = async () => {
    setLoadingTags(true);
    try {
      const res = await productTagsApi.list(resolvedProductId);
      setTags(res.data?.tags || []);
    } catch (err) {
      console.error('Failed to load tags:', err);
    } finally {
      setLoadingTags(false);
    }
  };
  
  // Helper for contrast color
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#1f2937' : '#ffffff';
  };
  
  // Tag actions
  const startEditTag = (tag) => {
    setEditingTag(tag.tag_id);
    setEditTagName(tag.name);
    setEditTagColor(tag.color);
  };
  
  const cancelEditTag = () => {
    setEditingTag(null);
    setEditTagName('');
    setEditTagColor('');
  };
  
  const saveTagEdit = async () => {
    if (!editingTag) return;
    
    try {
      await productTagsApi.update(resolvedProductId, editingTag, {
        name: editTagName,
        color: editTagColor
      });
      toast.success('Tag updated');
      loadTags();
      cancelEditTag();
    } catch (err) {
      toast.error('Failed to update tag');
    }
  };
  
  const createNewTag = async () => {
    if (!newTagName.trim()) {
      toast.error('Please enter a tag name');
      return;
    }
    
    setCreatingTag(true);
    try {
      await productTagsApi.create(resolvedProductId, {
        name: newTagName.trim(),
        color: newTagColor
      });
      toast.success(`Created tag "${newTagName.trim()}"`);
      setNewTagName('');
      setNewTagColor('#6366f1');
      setShowNewTagForm(false);
      loadTags();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error('Tag with this name already exists');
      } else {
        toast.error('Failed to create tag');
      }
    } finally {
      setCreatingTag(false);
    }
  };
  
  const deleteTag = async (tagId, force = false) => {
    try {
      await productTagsApi.delete(resolvedProductId, tagId, force);
      toast.success('Tag deleted');
      loadTags();
    } catch (err) {
      if (err.response?.status === 400 && !force) {
        // Tag is in use, ask for confirmation
        if (window.confirm('This tag is in use. Delete it anyway? It will be removed from all features.')) {
          deleteTag(tagId, true);
        }
      } else {
        toast.error('Failed to delete tag');
      }
    }
  };
  
  const toggleMergeSelection = (tagId) => {
    if (selectedForMerge.includes(tagId)) {
      setSelectedForMerge(selectedForMerge.filter(id => id !== tagId));
      if (mergeTarget === tagId) setMergeTarget(null);
    } else {
      setSelectedForMerge([...selectedForMerge, tagId]);
    }
  };
  
  const executeMerge = async () => {
    if (!mergeTarget || selectedForMerge.length < 2) return;
    
    const sourceIds = selectedForMerge.filter(id => id !== mergeTarget);
    
    try {
      await productTagsApi.merge(resolvedProductId, {
        source_tag_ids: sourceIds,
        target_tag_id: mergeTarget
      });
      toast.success(`Merged ${sourceIds.length} tags`);
      setSelectedForMerge([]);
      setMergeTarget(null);
      loadTags();
    } catch (err) {
      toast.error('Failed to merge tags');
    }
  };
  
  // Save product settings
  const saveProductSettings = async () => {
    setLoading(true);
    try {
      await productsApi.update(resolvedProductId, {
        card_color: cardColor,
        access_mode: accessMode,
        allowed_members: accessMode === 'selected' ? selectedMembers : [],
        settings: {
          ...(product?.settings || {}),
          goal_grace_period_days: goalGracePeriod,
          product_owner_id: productOwnerId || null,
          kpi_reminder_enabled: kpiReminderEnabled
        },
        navigation_settings: navigationSettings
      });
      toast.success('Settings saved');
      onProductUpdate?.();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  // Handle Start Over (archive product and navigate to wizard)
  const handleStartOver = async () => {
    setStartingOver(true);
    try {
      const productsRes = await productsApi.list(false);
      const activeProducts = productsRes.data?.products || productsRes.data || [];
      
      if (activeProducts.length > 0) {
        for (const p of activeProducts) {
          await productsApi.archive(p.product_id);
        }
      }
      
      toast.success('Product archived. Starting fresh!');
      setShowStartOverModal(false);
      onClose();
      navigate('/products/new');
    } catch (err) {
      console.error('Failed to start over:', err);
      toast.error('Failed to archive product. Please try again.');
    } finally {
      setStartingOver(false);
    }
  };
  
  // Use canEditNavigation to determine if navigation tab should be shown
  const showNavigationTab = (organization?.navigation_defaults?.allow_product_override !== false) || canEditNavigation;
  
  // Feature flag checks for tab visibility
  const hasIntegrations = isEnabled(FEATURE_IDS.INTEGRATION_GITHUB) || 
    isEnabled(FEATURE_IDS.INTEGRATION_JIRA) || 
    isEnabled(FEATURE_IDS.INTEGRATION_LINEAR) ||
    isEnabled(FEATURE_IDS.INTEGRATION_MCP);
  const hasTeam = isEnabled(FEATURE_IDS.ORG_TEAM);
  const hasGoals = isEnabled(FEATURE_IDS.TAB_GOALS);
  
  // Reorganized tabs with feature flag gating
  const tabs = [
    ...(!isFreeProduct ? [{ id: 'general', label: 'General', icon: User }] : []),
    ...(hasTeam ? [{ id: 'access', label: 'Access', icon: Users }] : []),
    ...(hasIntegrations ? [{ id: 'integrations', label: 'Integrations', icon: Plug }] : []),
    { id: 'tags', label: 'Tags', icon: Tag },
    ...(hasGoals ? [{ id: 'compliance', label: 'Goals & Compliance', icon: Target }] : []),
    { id: 'appearance', label: 'Appearance', icon: Palette },
    ...(!isFreeProduct && showNavigationTab ? [{ id: 'navigation', label: 'Navigation', icon: Compass }] : []),
    ...(isFreeProduct ? [{ id: 'reset', label: 'Reset Product', icon: RotateCcw }] : []),
    { id: 'export', label: 'Export', icon: Download },
  ];
  
  // Filter launch tabs based on navigation settings
  const currentNavSettings = navigationSettings || product?.navigation_settings || {};
  const availableLaunchTabs = LAUNCH_TABS.filter(tab => {
    // Strategy, Personas, Goals, Features, Epics are always available (core)
    const coreTabs = ['strategy', 'personas', 'goals', 'features', 'epics'];
    if (coreTabs.includes(tab.value)) return true;
    
    // Check module-specific tabs against navigation settings
    if (tab.value === 'ideation' && currentNavSettings.ideation_enabled === false) return false;
    if (tab.value === 'market' && currentNavSettings.market_enabled === false) return false;
    if (tab.value === 'releases' && currentNavSettings.planning_enabled === false) return false;
    
    return true;
  });
  
  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className={cn(
          "w-full max-w-4xl h-[80vh] rounded-xl shadow-2xl flex flex-col overflow-hidden",
          colors.bgCard
        )}>
          {/* Header */}
          <div className={cn("px-6 py-4 border-b flex items-center justify-between", colors.borderPrimary)}>
            <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
              Product Settings
            </h2>
            <button onClick={onClose} className={cn("p-2 rounded-lg", colors.bgHover)}>
              <X className={cn("w-5 h-5", colors.textMuted)} />
            </button>
          </div>
          
          {/* Content with Tabs */}
          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar Tabs */}
            <div className={cn("w-48 border-r py-2", colors.borderPrimary, colors.bgSecondary)}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full px-4 py-2 flex items-center gap-2 text-sm font-medium transition-colors",
                    activeTab === tab.id 
                      ? cn("bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-2 border-indigo-600")
                      : cn(colors.textSecondary, "hover:bg-gray-100 dark:hover:bg-gray-700")
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* General Tab - User-specific preferences */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <div>
                    <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Your Preferences</h3>
                    <p className={cn("text-sm mb-6", colors.textMuted)}>
                      Personalize your experience with this product. These settings only apply to you.
                    </p>
                    
                    {/* Default Launch Tab */}
                    <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgTertiary)}>
                      <Label className={cn("text-sm font-medium", colors.textPrimary)}>
                        Default Launch Tab
                      </Label>
                      <p className={cn("text-sm mb-3", colors.textMuted)}>
                        Choose which tab opens by default when you view this product.
                      </p>
                      
                      {loadingUserPrefs ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading preferences...
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <Select 
                            value={userDefaultTab || 'default'} 
                            onValueChange={(val) => setUserDefaultTab(val === 'default' ? null : val)}
                          >
                            <SelectTrigger className="w-64">
                              <SelectValue placeholder="Use product default" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">
                                Use product default
                              </SelectItem>
                              {availableLaunchTabs.map(tab => (
                                <SelectItem key={tab.value} value={tab.value}>
                                  {tab.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <Button
                            onClick={saveUserPreferences}
                            disabled={savingUserPrefs}
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            {savingUserPrefs ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                Save Preference
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Tags Tab */}
              {activeTab === 'tags' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Manage Tags</h3>
                      <p className={cn("text-sm", colors.textMuted)}>
                        View, edit, merge, or delete tags for this product.
                      </p>
                    </div>
                    {!showNewTagForm && (
                      <Button
                        size="sm"
                        onClick={() => setShowNewTagForm(true)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Tag
                      </Button>
                    )}
                  </div>
                  
                  {/* New Tag Form */}
                  {showNewTagForm && (
                    <div className={cn("p-4 rounded-lg border", "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800")}>
                      <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-3">
                        Create New Tag
                      </p>
                      <div className="flex items-center gap-3">
                        {/* Color picker */}
                        <div className="flex gap-1.5 items-center">
                          {CARD_COLORS.slice(0, 8).map(c => (
                            <button
                              key={c.value}
                              onClick={() => setNewTagColor(c.value)}
                              className={cn(
                                "w-6 h-6 rounded-full transition-transform hover:scale-110",
                                newTagColor === c.value && "ring-2 ring-offset-2 ring-gray-400"
                              )}
                              style={{ backgroundColor: c.value }}
                              title={c.name}
                            />
                          ))}
                          <label className="relative w-6 h-6 rounded-full cursor-pointer overflow-hidden hover:scale-110 transition-transform">
                            <input
                              type="color"
                              value={newTagColor}
                              onChange={(e) => setNewTagColor(e.target.value)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              title="Custom Color"
                            />
                            <div 
                              className="w-full h-full rounded-full border border-gray-300"
                              style={{ background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)` }}
                            />
                          </label>
                        </div>
                        
                        {/* Preview */}
                        <span 
                          className="px-2.5 py-1 text-sm rounded-full font-medium"
                          style={{ 
                            backgroundColor: newTagColor,
                            color: getContrastColor(newTagColor)
                          }}
                        >
                          {newTagName || 'Preview'}
                        </span>
                        
                        {/* Name input */}
                        <Input
                          value={newTagName}
                          onChange={(e) => setNewTagName(e.target.value)}
                          placeholder="Tag name..."
                          className="flex-1 h-9"
                          onKeyDown={(e) => e.key === 'Enter' && createNewTag()}
                        />
                        
                        {/* Actions */}
                        <Button
                          size="sm"
                          onClick={createNewTag}
                          disabled={creatingTag || !newTagName.trim()}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {creatingTag ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setShowNewTagForm(false);
                            setNewTagName('');
                            setNewTagColor('#6366f1');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Merge UI */}
                  {selectedForMerge.length >= 2 && (
                    <div className={cn("p-4 rounded-lg border", "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800")}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                            {selectedForMerge.length} tags selected for merge
                          </p>
                          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">
                            Select a target tag to merge into, then click &quot;Merge&quot;
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedForMerge([]);
                              setMergeTarget(null);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={executeMerge}
                            disabled={!mergeTarget}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <GitMerge className="w-4 h-4 mr-1" />
                            Merge
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Tags List */}
                  {loadingTags ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
                    </div>
                  ) : tags.length === 0 ? (
                    <div className={cn("text-center py-12", colors.textMuted)}>
                      <Tag className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No tags created yet</p>
                      <p className="text-sm mt-1">Tags can be created when editing features</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {tags.map(tag => (
                        <div
                          key={tag.tag_id}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                            selectedForMerge.includes(tag.tag_id) 
                              ? "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700"
                              : cn(colors.bgSecondary, colors.borderPrimary),
                            mergeTarget === tag.tag_id && "ring-2 ring-indigo-500"
                          )}
                        >
                          {/* Checkbox for merge selection */}
                          <button
                            type="button"
                            onClick={() => toggleMergeSelection(tag.tag_id)}
                            className={cn(
                              "w-5 h-5 rounded border flex items-center justify-center",
                              selectedForMerge.includes(tag.tag_id) 
                                ? "bg-indigo-600 border-indigo-600" 
                                : colors.borderPrimary
                            )}
                          >
                            {selectedForMerge.includes(tag.tag_id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </button>
                          
                          {editingTag === tag.tag_id ? (
                            // Edit Mode
                            <div className="flex-1 flex items-center gap-3">
                              <div className="flex gap-1.5 items-center">
                                {CARD_COLORS.slice(0, 8).map(c => (
                                  <button
                                    key={c.value}
                                    onClick={() => setEditTagColor(c.value)}
                                    className={cn(
                                      "w-6 h-6 rounded-full transition-transform hover:scale-110",
                                      editTagColor === c.value && "ring-2 ring-offset-2 ring-gray-400"
                                    )}
                                    style={{ backgroundColor: c.value }}
                                    title={c.name}
                                  />
                                ))}
                                {/* Custom color picker */}
                                <label className="relative w-6 h-6 rounded-full cursor-pointer overflow-hidden hover:scale-110 transition-transform">
                                  <input
                                    type="color"
                                    value={editTagColor || '#6366f1'}
                                    onChange={(e) => setEditTagColor(e.target.value)}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    title="Custom Color"
                                  />
                                  <div 
                                    className="w-full h-full rounded-full border border-gray-300"
                                    style={{ background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)` }}
                                  />
                                </label>
                              </div>
                              {/* Preview of selected color */}
                              <span 
                                className="px-2 py-0.5 text-xs rounded-full font-medium"
                                style={{ 
                                  backgroundColor: editTagColor,
                                  color: getContrastColor(editTagColor)
                                }}
                              >
                                Preview
                              </span>
                              <Input
                                value={editTagName}
                                onChange={(e) => setEditTagName(e.target.value)}
                                className="flex-1 h-8"
                              />
                              <Button size="sm" onClick={saveTagEdit}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEditTag}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            // View Mode
                            <>
                              <span
                                className="px-2.5 py-1 rounded-full text-sm font-medium"
                                style={{ 
                                  backgroundColor: tag.color,
                                  color: getContrastColor(tag.color)
                                }}
                              >
                                {tag.name}
                              </span>
                              <span className={cn("text-sm", colors.textMuted)}>
                                {tag.usage_count || 0} feature{(tag.usage_count || 0) !== 1 ? 's' : ''}
                              </span>
                              
                              <div className="flex-1" />
                              
                              {/* Target for merge button */}
                              {selectedForMerge.length >= 2 && selectedForMerge.includes(tag.tag_id) && (
                                <Button
                                  size="sm"
                                  variant={mergeTarget === tag.tag_id ? "default" : "outline"}
                                  onClick={() => setMergeTarget(tag.tag_id)}
                                  className="text-xs"
                                >
                                  {mergeTarget === tag.tag_id ? 'Target' : 'Set as Target'}
                                </Button>
                              )}
                              
                              <button
                                onClick={() => startEditTag(tag)}
                                className={cn("p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteTag(tag.tag_id)}
                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Appearance Tab */}
              {activeTab === 'appearance' && (
                <div className="space-y-6">
                  <div>
                    <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Card Color</h3>
                    <p className={cn("text-sm mb-4", colors.textMuted)}>
                      Choose a color for the product card on the dashboard.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {CARD_COLORS.map(color => (
                        <button
                          key={color.value}
                          onClick={() => setCardColor(color.value)}
                          className={cn(
                            "w-10 h-10 rounded-lg transition-transform hover:scale-110",
                            cardColor === color.value && "ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-500"
                          )}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                      <label className="w-10 h-10 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center cursor-pointer hover:border-gray-400 overflow-hidden">
                        <input
                          type="color"
                          value={cardColor}
                          onChange={(e) => setCardColor(e.target.value)}
                          className="absolute opacity-0 cursor-pointer"
                        />
                        <div 
                          className="w-full h-full"
                          style={{ background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)` }}
                        />
                      </label>
                    </div>
                    
                    {/* Preview */}
                    <div className="mt-6">
                      <Label className={colors.textMuted}>Preview</Label>
                      <div 
                        className="mt-2 w-48 h-24 rounded-lg p-4 flex flex-col justify-end"
                        style={{ backgroundColor: cardColor }}
                      >
                        <p className="text-white font-medium text-sm truncate" style={{ color: getContrastColor(cardColor) }}>
                          {product?.name || 'Product Name'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Navigation Tab */}
              {activeTab === 'navigation' && showNavigationTab && (
                <div className="space-y-6">
                  {/* Module visibility settings */}
                  <div>
                    <ProductNavigationSettings
                      product={product}
                      organization={organization}
                      canEdit={canEditNavigation}
                      onChange={setNavigationSettings}
                    />
                  </div>
                </div>
              )}
              
              {/* Integrations Tab */}
              {activeTab === 'integrations' && (
                <ProductIntegrationsSection
                  productId={resolvedProductId}
                  colors={colors}
                  canEdit={canEditNavigation}
                />
              )}
              
              {/* Access Tab */}
              {activeTab === 'access' && (
                <div className="space-y-6">
                  <div>
                    <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Product Access</h3>
                    <p className={cn("text-sm mb-4", colors.textMuted)}>
                      Control who can view and work on this product.
                    </p>
                    
                    <div className="space-y-3">
                      <label className={cn(
                        "flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors",
                        accessMode === 'everyone' ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : colors.borderPrimary
                      )}>
                        <input
                          type="radio"
                          name="access"
                          value="everyone"
                          checked={accessMode === 'everyone'}
                          onChange={() => setAccessMode('everyone')}
                          className="text-indigo-600"
                        />
                        <div>
                          <p className={cn("font-medium", colors.textPrimary)}>Everyone</p>
                          <p className={cn("text-sm", colors.textMuted)}>All team members can access this product</p>
                        </div>
                      </label>
                      
                      <label className={cn(
                        "flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors",
                        accessMode === 'selected' ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" : colors.borderPrimary
                      )}>
                        <input
                          type="radio"
                          name="access"
                          value="selected"
                          checked={accessMode === 'selected'}
                          onChange={() => setAccessMode('selected')}
                          className="text-indigo-600 mt-1"
                        />
                        <div className="flex-1">
                          <p className={cn("font-medium", colors.textPrimary)}>Selected Members</p>
                          <p className={cn("text-sm mb-3", colors.textMuted)}>Only specific team members can access</p>
                          
                          {accessMode === 'selected' && (
                            <AccessMemberSelector
                              teamMembers={teamMembers}
                              selectedMembers={selectedMembers}
                              setSelectedMembers={setSelectedMembers}
                              currentUserId={user?.user_id}
                              colors={colors}
                            />
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Stability Tab */}
              {activeTab === 'compliance' && hasGoals && (
                <div className="space-y-6">
                  <div>
                    <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Goals & Compliance</h3>
                    <p className={cn("text-sm", colors.textMuted)}>
                      Configure goal stability rules and KPI data entry compliance settings.
                    </p>
                  </div>
                  
                  {/* Product Owner Section */}
                  <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgTertiary)}>
                    <div className="flex items-start gap-4">
                      <User className={cn("w-8 h-8 mt-1", colors.textAccent)} />
                      <div className="flex-1">
                        <Label className={cn("text-base font-medium", colors.textPrimary)}>
                          Product Owner
                        </Label>
                        <p className={cn("text-sm mt-1 mb-4", colors.textMuted)}>
                          The product owner is responsible for ensuring KPI data is entered on time.
                          They will receive reminders when KPI data entry is due.
                        </p>
                        
                        <Select 
                          value={productOwnerId || '__none__'} 
                          onValueChange={(v) => setProductOwnerId(v === '__none__' ? '' : v)}
                        >
                          <SelectTrigger className={cn("w-64", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder="Select product owner..." />
                          </SelectTrigger>
                          <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                            <SelectItem value="__none__">No owner assigned</SelectItem>
                            {teamMembers.map(member => (
                              <SelectItem key={member.user_id} value={member.user_id}>
                                {member.name || member.email}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  {/* KPI Compliance Info - moved above KPI Reminders */}
                  <div className={cn("p-4 rounded-lg border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20")}>
                    <h4 className={cn("text-sm font-medium text-blue-800 dark:text-blue-300 mb-2")}>
                      About KPI Data Entry Compliance
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Each KPI has a measurement interval (Weekly, Monthly, or Quarterly). Compliance is measured
                      by whether data has been entered for each completed period since the goal was activated.
                      The dashboard shows overall compliance and allows drilling down into non-compliant KPIs.
                    </p>
                  </div>
                  
                  {/* KPI Reminders Section */}
                  <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgTertiary)}>
                    <div className="flex items-start gap-4">
                      <Shield className={cn("w-8 h-8 mt-1", colors.textAccent)} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className={cn("text-base font-medium", colors.textPrimary)}>
                              KPI Data Entry Reminders
                            </Label>
                            <p className={cn("text-sm mt-1", colors.textMuted)}>
                              Send notifications to the product owner 72 hours before KPI data entry is due.
                            </p>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={kpiReminderEnabled}
                            onClick={() => setKpiReminderEnabled(!kpiReminderEnabled)}
                            className={cn(
                              "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2",
                              kpiReminderEnabled ? "bg-indigo-600" : "bg-gray-200 dark:bg-gray-700"
                            )}
                          >
                            <span
                              className={cn(
                                "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                                kpiReminderEnabled ? "translate-x-5" : "translate-x-0"
                              )}
                            />
                          </button>
                        </div>
                        
                        {kpiReminderEnabled && !productOwnerId && (
                          <p className="text-sm text-amber-600 dark:text-amber-400 mt-3">
                            Note: You must assign a product owner to receive reminders.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Goal Shift Info - moved above Grace Period */}
                  <div className={cn("p-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20")}>
                    <h4 className={cn("text-sm font-medium text-amber-800 dark:text-amber-300 mb-2")}>
                      What counts as a &quot;Goal Shift&quot;?
                    </h4>
                    <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside">
                      <li>Moving a goal from Active/Achieved back to Draft</li>
                      <li>Then editing KPI targets, names, or formats after the grace period</li>
                      <li>Deleting KPIs after the grace period</li>
                    </ul>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">
                      Shifted goals are permanently tagged to maintain accountability and transparency.
                    </p>
                  </div>
                  
                  {/* Goal Stability Grace Period */}
                  <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgTertiary)}>
                    <div className="flex items-start gap-4">
                      <Target className={cn("w-8 h-8 mt-1", colors.textAccent)} />
                      <div className="flex-1">
                        <Label className={cn("text-base font-medium", colors.textPrimary)}>
                          Goal Edit Grace Period
                        </Label>
                        <p className={cn("text-sm mt-1 mb-4", colors.textMuted)}>
                          After a goal moves back to Draft status, team members have this amount of time 
                          to make changes without the goal being flagged as &quot;Shifted&quot;. Changes made 
                          outside this window will be logged in the Stability Log.
                        </p>
                        
                        <Select 
                          value={goalGracePeriod.toString()} 
                          onValueChange={(v) => setGoalGracePeriod(parseInt(v))}
                        >
                          <SelectTrigger className={cn("w-48", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue placeholder="Select period..." />
                          </SelectTrigger>
                          <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                            {GRACE_PERIOD_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value.toString()}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Reset Product Tab - Free Tier Only */}
              {activeTab === 'reset' && isFreeProduct && (
                <div className="space-y-6">
                  <div>
                    <h3 className={cn("text-lg font-medium mb-1", colors.textPrimary)}>Reset Product</h3>
                    <p className={cn("text-sm mb-6", colors.textMuted)}>
                      Archive your current product and start fresh with a new idea. Your archived product data will be preserved but you'll begin the Product Wizard again.
                    </p>
                    
                    <Button
                      variant="outline"
                      data-testid="start-over-button"
                      className="border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                      onClick={() => setShowStartOverModal(true)}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Start Over with New Idea
                    </Button>
                  </div>
                  
                  {/* Start Over Confirmation Dialog */}
                  {showStartOverModal && (
                    <Dialog open={showStartOverModal} onOpenChange={setShowStartOverModal}>
                      <DialogContent className={cn(colors.bgCard)}>
                        <DialogHeader>
                          <DialogTitle className={cn(colors.textPrimary)}>Start Over?</DialogTitle>
                          <DialogDescription className={cn(colors.textMuted)}>
                            Your current product will be archived. You can always view archived products later, but you'll start fresh with the Product Wizard.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-2">
                          <Button
                            variant="outline"
                            data-testid="start-over-cancel"
                            onClick={() => setShowStartOverModal(false)}
                            disabled={startingOver}
                          >
                            Cancel
                          </Button>
                          <Button
                            data-testid="start-over-confirm"
                            className="bg-amber-500 hover:bg-amber-600 text-black"
                            onClick={handleStartOver}
                            disabled={startingOver}
                          >
                            {startingOver ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Archiving...</>
                            ) : (
                              <><RotateCcw className="w-4 h-4 mr-2" />Start Over</>
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              )}

              {/* Export Tab */}
              {activeTab === 'export' && (
                <ExportTabContent productId={resolvedProductId} productName={product?.name} colors={colors} />
              )}
            </div>
          </div>
          <div className={cn("px-6 py-4 border-t flex justify-end gap-3", colors.borderPrimary)}>
            <Button variant="outline" onClick={onClose} className={cn("border", colors.borderPrimary)}>
              Cancel
            </Button>
            <Button 
              onClick={saveProductSettings}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSettingsModal;
